import React, { useState, useEffect } from 'react';
import { withStyles } from '@material-ui/core/styles';
import { useDropzone } from 'react-dropzone';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, LinearProgress } from '@material-ui/core';
import PropTypes from 'prop-types';

// SERVICES
import { submitAssignmentImport } from '../../services';
import { downloadExcelFileToBrowser } from '../../helpers/excelFileDownload';

const styles = () => ({
  dropzone: {
    backgroundColor: '#F8F8F8',
    borderStyle: 'dashed',
    borderWidth: 2,
    borderColor: 'rgba(0,0,0,0.1)',
    color: 'rgba(0,0,0,0.4)',
    padding: 20,
    textAlign: 'center',
    cursor: 'pointer',
  },
  columnTable: {
    borderCollapse: 'collapse',
    marginBottom: 15,
    '& td, & th': {
      border: '1px solid rgba(0,0,0,0.15)',
      padding: '4px 10px',
      textAlign: 'left',
    },
  },
  summary: {
    marginTop: 15,
    fontWeight: 'bold',
  },
  error: {
    marginTop: 15,
    color: '#c62828',
  },
});

export const ImportAssignmentDialog = withStyles(styles)(props => {
  const { classes, show, onClose } = props;
  const [file, setFile] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);

  useEffect(() => {
    // reset every time the dialog opens
    if (show) {
      setFile(null);
      setResult(null);
      setErrorMessage(null);
    }
  }, [show]);

  const { getRootProps, getInputProps } = useDropzone({
    accept: '.xls, .xlsx',
    multiple: false,
    disabled: isSubmitting,
    onDrop: acceptedFiles => {
      if (acceptedFiles.length > 0) {
        setFile(acceptedFiles[0]);
        setResult(null);
        setErrorMessage(null);
      }
    },
  });

  const handleImportClick = async () => {
    if (!file) return;
    setIsSubmitting(true);
    setErrorMessage(null);
    try {
      const response = await submitAssignmentImport(file);
      setResult(response);
      downloadExcelFileToBrowser(response.fileData, response.fileName);
    } catch (err) {
      let message = 'The file could not be imported.';
      try {
        message = (await err.text()) || message;
      } catch (e) {
        // network error - keep default message
      }
      setErrorMessage(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (isSubmitting) return;
    // refresh the grid when anything was assigned
    onClose(!!result && result.succeeded > 0);
  };

  return (
    <Dialog open={show} onClose={handleClose} fullWidth maxWidth="sm" aria-labelledby="import-assignment-title">
      <DialogTitle id="import-assignment-title">Import Assignment</DialogTitle>
      <DialogContent>
        <p>Upload an Excel file (.xlsx or .xls) with these column headers in the first row:</p>
        <table className={classes.columnTable}>
          <thead>
            <tr>
              <th>OrderLineId</th>
              <th>FieldRepCode</th>
              <th>FieldRepStipend (optional)</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>123456</td>
              <td>ABC123</td>
              <td>25.00</td>
            </tr>
          </tbody>
        </table>
        <p>
          Each valid order line will be assigned to the field rep. When <b>FieldRepStipend</b> is filled in, the order's
          field rep stipend is added or updated to that amount. If it is left blank, any existing stipend is removed when
          the order is reassigned, the same as Bulk Edit.
        </p>
        <p>
          A copy of your file will download with a <b>Result</b> column (Success / Failed) and a <b>Reason</b> column
          explaining any failures.
        </p>

        <div {...getRootProps({ className: classes.dropzone })}>
          <input {...getInputProps()} />
          {file ? <p>{file.name}</p> : <p>Drag 'n' drop a file here, or click to select a file</p>}
        </div>

        {result && (
          <div className={classes.summary}>
            {result.totalRows} rows processed: {result.succeeded} succeeded, {result.failed} failed.
            <div>
              <Button
                size="small"
                color="primary"
                onClick={() => downloadExcelFileToBrowser(result.fileData, result.fileName)}
              >
                Download results again
              </Button>
            </div>
          </div>
        )}
        {errorMessage && <div className={classes.error}>{errorMessage}</div>}
      </DialogContent>
      <DialogActions style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
        <Button
          onClick={handleClose}
          disabled={isSubmitting}
          variant="contained"
          style={{ backgroundColor: '#315382', color: 'white' }}
        >
          {result ? 'Close' : 'Cancel'}
        </Button>
        <div style={{ flex: 1, paddingLeft: 10, paddingRight: 10 }}>
          {isSubmitting && (
            <div>
              <div className="text-center">Assigning orders...</div>
              <LinearProgress />
            </div>
          )}
        </div>
        <Button
          disabled={!file || isSubmitting}
          onClick={handleImportClick}
          variant="contained"
          style={{ backgroundColor: !file || isSubmitting ? undefined : '#315382', color: 'white' }}
        >
          Import
        </Button>
      </DialogActions>
    </Dialog>
  );
});

ImportAssignmentDialog.propTypes = {
  show: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};
